import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { MinioObserveDetails } from "../../../api/observeRpcApi";
import {
  deleteObserveInstance,
  fetchObserveDetails,
  updateObserveInstance,
} from "./observeDetailsThunks";
import { MinIOCredentials } from "../../common/types";

export interface ObserveState {
  details: MinioObserveDetails | null;
  imageRegistry: string;
  imageRegistryPassword: string;
  imageRegistryUsername: string;
  isCustomRegistry: boolean;
  isCustomRegistryCreds: boolean;
  loadingDeleteObserve: boolean;
  loadingObserveDetails: boolean;
  loadingUpdateObserve: boolean;
  minioCredentials?: MinIOCredentials;
  observeDeleted: boolean;
  openDeleteObserveConfirmModal: boolean;
  openUpdateObserveConfirmModal: boolean;
  resourcesCPULimitError: string;
  resourcesCPURequestError: string;
  resourcesMemoryLimitError: string;
  resourcesMemoryRequestError: string;
  resourcesSpecifyLimit: boolean;
  volumeSizeFactor: string;
  volumeSizeFactorDB: string;
}

const initialState: ObserveState = {
  details: null,
  imageRegistry: "",
  imageRegistryPassword: "",
  imageRegistryUsername: "",
  isCustomRegistry: false,
  isCustomRegistryCreds: false,
  loadingDeleteObserve: false,
  loadingObserveDetails: false,
  loadingUpdateObserve: false,
  observeDeleted: false,
  openDeleteObserveConfirmModal: false,
  openUpdateObserveConfirmModal: false,
  resourcesCPULimitError: "",
  resourcesCPURequestError: "",
  resourcesMemoryLimitError: "",
  resourcesMemoryRequestError: "",
  resourcesSpecifyLimit: false,
  volumeSizeFactor: "Gi",
  volumeSizeFactorDB: "Gi",
};

export const observeSlice = createSlice({
  name: "observe",
  initialState,
  reducers: {
    setLoadingObserveDetails: (state, action: PayloadAction<boolean>) => {
      state.loadingObserveDetails = action.payload;
    },
    setOpenDeleteObserveConfirmModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.openDeleteObserveConfirmModal = action.payload;
    },
    setOpenUpdateObserveConfirmModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.openUpdateObserveConfirmModal = action.payload;
    },
    setLoadingDeleteObserve: (state, action: PayloadAction<boolean>) => {
      state.loadingDeleteObserve = action.payload;
    },
    setObserveDeleted: (state, action: PayloadAction<boolean>) => {
      state.observeDeleted = action.payload;
    },
    setObserveDetails: (
      state,
      action: PayloadAction<MinioObserveDetails | null>,
    ) => {
      state.details = action.payload;
    },
    setObserveVolumeSizeFactor: (state, action: PayloadAction<string>) => {
      state.volumeSizeFactor = action.payload;
    },
    setObserveCPULimitError: (state, action: PayloadAction<string>) => {
      state.resourcesCPULimitError = action.payload;
    },
    setObserveMemoryLimitError: (state, action: PayloadAction<string>) => {
      state.resourcesMemoryLimitError = action.payload;
    },
    setObserveCPURequestError: (state, action: PayloadAction<string>) => {
      state.resourcesCPURequestError = action.payload;
    },
    setObserveMemoryRequestError: (state, action: PayloadAction<string>) => {
      state.resourcesMemoryRequestError = action.payload;
    },
    setObserveResourcesSpecifyLimit: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.resourcesSpecifyLimit = action.payload;
    },
    setObserveMinIOCredentials: (
      state,
      action: PayloadAction<MinIOCredentials>,
    ) => {
      state.minioCredentials = action.payload;
    },
    setObserveIsCustomRegistry: (state, action: PayloadAction<boolean>) => {
      state.isCustomRegistry = action.payload;
    },
    setObserveIsCustomRegistryCreds: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isCustomRegistryCreds = action.payload;
    },
    setObserveImageRegistry: (state, action: PayloadAction<string>) => {
      state.imageRegistry = action.payload;
    },
    setObserveImageRegistryPassword: (state, action: PayloadAction<string>) => {
      state.imageRegistryPassword = action.payload;
    },
    setObserveImageRegistryUsername: (state, action: PayloadAction<string>) => {
      state.imageRegistryUsername = action.payload;
    },
    setObserveVolumeSizeFactorDB: (state, action: PayloadAction<string>) => {
      state.volumeSizeFactorDB = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Observe Details
      .addCase(fetchObserveDetails.fulfilled, (state, action) => {
        state.loadingObserveDetails = false;
        state.details = action.payload;
        // Reset ImageRegistry Switches and content
        state.imageRegistry = "";
        state.imageRegistryUsername = "";
        state.imageRegistryPassword = "";
        state.isCustomRegistry = false;
        state.isCustomRegistryCreds = false;
      })
      .addCase(fetchObserveDetails.pending, (state) => {
        state.loadingObserveDetails = true;
      })
      .addCase(fetchObserveDetails.rejected, (state) => {
        state.loadingObserveDetails = false;
        state.details = null;
      })
      // Delete Observe Instance
      .addCase(deleteObserveInstance.pending, (state) => {
        state.loadingDeleteObserve = true;
      })
      .addCase(deleteObserveInstance.fulfilled, (state) => {
        state.loadingDeleteObserve = false;
        state.observeDeleted = true;
      })
      .addCase(deleteObserveInstance.rejected, (state) => {
        state.loadingDeleteObserve = false;
      })
      // Update Observe Instance
      .addCase(updateObserveInstance.pending, (state) => {
        state.loadingUpdateObserve = true;
      })
      .addCase(updateObserveInstance.fulfilled, (state) => {
        state.loadingUpdateObserve = false;
      })
      .addCase(updateObserveInstance.rejected, (state) => {
        state.loadingUpdateObserve = false;
      });
  },
});

export const {
  setLoadingDeleteObserve,
  setLoadingObserveDetails,
  setObserveCPULimitError,
  setObserveCPURequestError,
  setObserveDeleted,
  setObserveDetails,
  setObserveImageRegistry,
  setObserveImageRegistryPassword,
  setObserveImageRegistryUsername,
  setObserveIsCustomRegistry,
  setObserveIsCustomRegistryCreds,
  setObserveMemoryLimitError,
  setObserveMemoryRequestError,
  setObserveMinIOCredentials,
  setObserveResourcesSpecifyLimit,
  setObserveVolumeSizeFactor,
  setObserveVolumeSizeFactorDB,
  setOpenDeleteObserveConfirmModal,
  setOpenUpdateObserveConfirmModal,
} = observeSlice.actions;

export default observeSlice.reducer;
